import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { useUpdateService } from "@/hooks/useServices";
import { useVendors } from "@/hooks/useVendors";
import { useServiceCategories, useServiceSubCategories, useServiceTypes } from "@/hooks/useServiceCategories";
import { PhotoUploader } from "@/components/ui/PhotoUploader";
import { PhotoGallery } from "@/components/ui/PhotoGallery";
import { PhotoParentType } from "@/types/photos";
import { usePhotos } from "@/hooks/usePhotos";
import type { ServiceResponse, UpdateServiceRequest } from "@/types/services";

interface EditServiceModalProps {
  open: boolean;
  onClose: () => void;
  service: ServiceResponse;
}

export default function EditServiceModal({ open, onClose, service }: EditServiceModalProps) {
  const queryClient = useQueryClient();
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");
  const { photos } = usePhotos(PhotoParentType.Service, service.id);

  const { register, handleSubmit, setValue, watch, reset, formState: { errors } } = useForm<UpdateServiceRequest>({
    defaultValues: {
      name: service.name || "",
      description: service.description || "",
      notes: service.notes || "",
      serviceCategoryId: service.serviceCategoryId || "",
      serviceSubCategoryId: service.serviceSubCategoryId || "",
      organizationId: service.organizationId || undefined,
      isActive: service.isActive
    }
  });

  const updateService = useUpdateService();
  const { data: categories, isLoading: categoriesLoading } = useServiceCategories();
  const { data: subcategories, isLoading: subcategoriesLoading } = useServiceSubCategories(selectedCategoryId);

  const selectedServiceCategoryId = watch("serviceCategoryId");
  const selectedServiceSubCategoryId = watch("serviceSubCategoryId");
  const isActive = watch("isActive");

  // Reset subcategory selection when category changes
  useEffect(() => {
    if (selectedServiceCategoryId !== selectedCategoryId) {
      setSelectedCategoryId(selectedServiceCategoryId);
      setValue("serviceSubCategoryId", "");
    }
  }, [selectedServiceCategoryId, selectedCategoryId, setValue]);

  // Initialize form with service data
  useEffect(() => {
    if (service && open) {
      setValue("name", service.name || "");
      setValue("description", service.description || "");
      setValue("notes", service.notes || "");
      setValue("serviceCategoryId", service.serviceCategoryId || "");
      setValue("serviceSubCategoryId", service.serviceSubCategoryId || "");
      setValue("organizationId", service.organizationId || undefined);
      setValue("isActive", service.isActive);
      setSelectedCategoryId(service.serviceCategoryId || "");
    }
  }, [service, open, setValue]);

  const onSubmit = async (data: UpdateServiceRequest) => {
    try {
      await updateService.mutateAsync({ id: service.id, data });
      toast({
        title: "Service updated",
        description: "Service has been updated successfully.",
      });
      onClose();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to update service. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleClose = () => {
    reset();
    setSelectedCategoryId("");
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Service</DialogTitle>
          <DialogDescription>
            Update the service details. Required fields are marked with an asterisk (*).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Basic Information</h3>

            <div className="space-y-2">
              <Label htmlFor="name">Service Name *</Label>
              <Input
                id="name"
                {...register("name", { required: "Service name is required" })}
                placeholder="Enter service name"
                maxLength={255}
                required
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                {...register("description")}
                placeholder="Enter service description"
                rows={3}
              />
            </div>
          </div>

          {/* Classification */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Service Classification</h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Service Category *</Label>
                <Select
                  value={selectedServiceCategoryId || ""}
                  onValueChange={(value) => setValue("serviceCategoryId", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {categoriesLoading ? (
                      <SelectItem value="loading" disabled>Loading categories...</SelectItem>
                    ) : (
                      categories?.map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.serviceCategoryId && (
                  <p className="text-sm text-destructive">{errors.serviceCategoryId.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="subcategory">Service Subcategory *</Label>
                <Select
                  value={selectedServiceSubCategoryId || ""}
                  onValueChange={(value) => setValue("serviceSubCategoryId", value)}
                  disabled={!selectedServiceCategoryId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={selectedServiceCategoryId ? "Select subcategory" : "Select category first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {subcategoriesLoading ? (
                      <SelectItem value="loading" disabled>Loading subcategories...</SelectItem>
                    ) : (
                      subcategories?.map((subcategory) => (
                        <SelectItem key={subcategory.id} value={subcategory.id}>
                          {subcategory.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.serviceSubCategoryId && (
                  <p className="text-sm text-destructive">{errors.serviceSubCategoryId.message}</p>
                )}
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Additional Information</h3>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                {...register("notes")}
                placeholder="Enter any additional notes"
                rows={2}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isActive"
                checked={isActive}
                onCheckedChange={(checked) => setValue("isActive", checked)}
              />
              <Label htmlFor="isActive" className="text-sm font-medium">
                Active
              </Label>
            </div>
          </div>

          {/* Photos */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-muted-foreground">Service Photos</h3>

            {photos.length > 0 ? (
              <div className="space-y-4">
                <PhotoGallery
                  photos={photos}
                  parentType={PhotoParentType.Service}
                  parentId={service.id}
                  editable={true}
                />
                <Separator />
                <div>
                  <Label className="mb-2 block">Upload Additional Photos</Label>
                  <PhotoUploader
                    parentType={PhotoParentType.Service}
                    parentId={service.id}
                    maxPhotos={10}
                    maxFileSizeMB={10}
                    onUploadComplete={() => {
                      queryClient.invalidateQueries({
                        queryKey: ["/photos", PhotoParentType.Service, service.id]
                      });
                    }}
                  />
                </div>
              </div>
            ) : (
              <div>
                <Label className="mb-2 block">Upload Photos</Label>
                <PhotoUploader
                  parentType={PhotoParentType.Service}
                  parentId={service.id}
                  maxPhotos={10}
                  maxFileSizeMB={10}
                  onUploadComplete={() => {
                    queryClient.invalidateQueries({
                      queryKey: ["/photos", PhotoParentType.Service, service.id]
                    });
                  }}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={updateService.isPending}>
              {updateService.isPending ? "Updating..." : "Update Service"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}